import { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Eye, EyeOff, Star, StarOff } from 'lucide-react';
import api from '@/services/api';
import { productService } from '@/services/product.service';
import { Product, Category, Brand } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/utils';
import toast from '@/hooks/useToast';

export const AdminProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', sku: '', price: '', discountedPrice: '', stock: '', categoryId: '', brandId: '', requiresPrescription: false, isFeatured: false, shortDesc: '', description: '', dosageForm: '', manufacturer: '', drugSchedule: '' });

  const fetchProducts = (pg = 1, q = '') => {
    setLoading(true);
    const url = q ? `/products/search?q=${encodeURIComponent(q)}&limit=20` : `/products?page=${pg}&limit=20`;
    api.get(url).then(res => {
      const d = res.data;
      setProducts(q ? d.data.products : d.data);
      setTotal(d.pagination?.total || 0);
    }).finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchProducts(1, search);
  }, [search]);

  useEffect(() => {
    fetchProducts(page);
    Promise.all([api.get('/categories'), api.get('/brands')]).then(([c, b]) => {
      setCategories(flattenCategories(c.data.data));
      setBrands(b.data.data);
    });
  }, [page]);

  const flattenCategories = (cats: Category[], depth = 0): Category[] =>
    cats.flatMap(c => [{ ...c, name: '  '.repeat(depth) + c.name }, ...flattenCategories(c.children || [], depth + 1)]);

  const toggleActive = async (id: string, current: boolean) => {
    await productService.update(id, { isActive: !current } as Partial<Product>);
    setProducts(p => p.map(prod => prod.id === id ? { ...prod, isActive: !current } : prod));
    toast.success(current ? 'Product deactivated' : 'Product activated');
  };

  const toggleFeatured = async (id: string, current: boolean) => {
    await productService.update(id, { isFeatured: !current } as Partial<Product>);
    setProducts(p => p.map(prod => prod.id === id ? { ...prod, isFeatured: !current } : prod));
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await productService.create(Object.assign(new FormData(), form) as FormData);
      toast.success('Product created!');
      setShowForm(false);
      fetchProducts();
    } catch { toast.error('Could not create product'); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-bold text-2xl text-gray-800">Products Management</h1>
        <Button onClick={() => setShowForm(!showForm)}><Plus className="h-4 w-4 mr-2" />Add Product</Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-card shadow-card p-6 mb-6">
          <h2 className="font-heading font-semibold text-lg text-gray-800 mb-4">New Product</h2>
          <form onSubmit={handleCreate}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <Input label="Product Name *" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required />
              <Input label="SKU *" value={form.sku} onChange={e => setForm(p => ({ ...p, sku: e.target.value }))} required />
              <Input label="Price (₹) *" type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} required />
              <Input label="Discounted Price (₹)" type="number" value={form.discountedPrice} onChange={e => setForm(p => ({ ...p, discountedPrice: e.target.value }))} />
              <Input label="Stock *" type="number" value={form.stock} onChange={e => setForm(p => ({ ...p, stock: e.target.value }))} required />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                <select className="input-base" value={form.categoryId} onChange={e => setForm(p => ({ ...p, categoryId: e.target.value }))} required>
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                <select className="input-base" value={form.brandId} onChange={e => setForm(p => ({ ...p, brandId: e.target.value }))}>
                  <option value="">Select brand</option>
                  {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <Input label="Dosage Form" value={form.dosageForm} onChange={e => setForm(p => ({ ...p, dosageForm: e.target.value }))} placeholder="tablet, syrup, capsule..." />
              <Input label="Manufacturer" value={form.manufacturer} onChange={e => setForm(p => ({ ...p, manufacturer: e.target.value }))} />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Drug Schedule</label>
                <select className="input-base" value={form.drugSchedule} onChange={e => setForm(p => ({ ...p, drugSchedule: e.target.value }))}>
                  <option value="">None</option>
                  <option value="H">Schedule H</option>
                  <option value="H1">Schedule H1</option>
                  <option value="X">Schedule X</option>
                </select>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
              <input className="input-base" value={form.shortDesc} onChange={e => setForm(p => ({ ...p, shortDesc: e.target.value }))} />
            </div>
            <div className="flex gap-6 mb-4">
              <label className="flex items-center gap-2 cursor-pointer text-sm"><input type="checkbox" className="accent-primary-500" checked={form.requiresPrescription} onChange={e => setForm(p => ({ ...p, requiresPrescription: e.target.checked }))} /> Requires Prescription</label>
              <label className="flex items-center gap-2 cursor-pointer text-sm"><input type="checkbox" className="accent-primary-500" checked={form.isFeatured} onChange={e => setForm(p => ({ ...p, isFeatured: e.target.checked }))} /> Featured</label>
            </div>
            <div className="flex gap-3">
              <Button type="submit">Create Product</Button>
              <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-card shadow-card p-4 mb-4 flex items-center gap-3">
        <Search className="h-4 w-4 text-gray-400" />
        <input
          placeholder="Search products by name, SKU..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 outline-none text-sm text-gray-700"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-card shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 text-gray-400 font-medium">Product</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium">SKU</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium">Price</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium">Stock</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium">Status</th>
                <th className="text-left px-4 py-3 text-gray-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array(8).fill(0).map((_, i) => (
                  <tr key={i}><td colSpan={6} className="px-4 py-3"><div className="h-6 bg-gray-100 rounded animate-pulse" /></td></tr>
                ))
              ) : products.map(p => (
                <tr key={p.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img src={p.images?.[0] || '/placeholder-product.jpg'} alt={p.name} className="w-10 h-10 object-cover rounded-lg" />
                      <div>
                        <p className="font-medium text-gray-800 truncate max-w-[180px]">{p.name}</p>
                        {p.requiresPrescription && <Badge variant="rx" className="text-xs mt-0.5">Rx</Badge>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs">{p.sku}</td>
                  <td className="px-4 py-3 font-semibold text-gray-800">
                    {formatCurrency(Number(p.discountedPrice || p.price))}
                    {p.discountedPrice && <span className="text-xs text-gray-400 line-through ml-1">{formatCurrency(Number(p.price))}</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-semibold ${p.stock < 10 ? 'text-orange-600' : 'text-gray-700'}`}>{p.stock}</span>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={p.isActive ? 'success' : 'secondary'}>{p.isActive ? 'Active' : 'Inactive'}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => toggleFeatured(p.id, p.isFeatured)} title={p.isFeatured ? 'Unfeature' : 'Feature'} className={`p-1.5 rounded-lg transition-colors ${p.isFeatured ? 'text-accent-500 bg-accent-50' : 'text-gray-400 hover:bg-gray-100'}`}>
                        {p.isFeatured ? <Star className="h-4 w-4" fill="currentColor" /> : <StarOff className="h-4 w-4" />}
                      </button>
                      <button onClick={() => toggleActive(p.id, p.isActive)} title={p.isActive ? 'Deactivate' : 'Activate'} className={`p-1.5 rounded-lg transition-colors ${p.isActive ? 'text-green-500 bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}>
                        {p.isActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!search && total > 20 && (
          <div className="flex justify-center gap-2 p-4">
            {Array.from({ length: Math.min(Math.ceil(total / 20), 7) }, (_, i) => i + 1).map(pg => (
              <button key={pg} onClick={() => setPage(pg)} className={`w-8 h-8 rounded-lg text-sm font-medium ${pg === page ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-700'}`}>{pg}</button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
